<template>
  <div class="page-box">
    <div class="hint">
      <img class="logo" src="https://agent.fskeeper.com/img/wechat_video.png" alt="视频号" />
      <div class="tip">进入视频号</div>
      <div class="sub">点击下方按钮打开视频号</div>
    </div>

    <!-- 微信限制开放标签必须由用户手势触发，无法自动弹起，故提供一个按钮供用户点击。
         内部 wxtag-template 用 v-html 注入，避免被 vue 模板编译器当作 <script> 删除 -->
    <wx-open-launch-weapp
      v-if="ready"
      :appid="weappAppid"
      :path="weappPath"
      :env-version="weappEnvVersion"
      class="launch-wrap"
      @launch="onLaunch"
      @error="onError"
      v-html="tagTemplate"
    ></wx-open-launch-weapp>

    <div v-if="errMsg" class="err">{{ errMsg }}</div>
  </div>
</template>

<script>
  import { getJsapiSignature, getChannelsConfig } from '@/api/api'

  // 小程序 appid（中转页所在小程序）
  const WEAPP_APPID = 'wxaf295c095afb6cb2'
  // 中转页路径（小程序内）
  const WEAPP_PATH = 'pages-common/channels/redirect'
  // 小程序版本：release 正式版 / trial 体验版 / develop 开发版
  const WEAPP_ENV_VERSION = 'release'
  const JSSDK_SRC = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'

  export default {
    data() {
      return {
        // 视频号参数由后端按订单号返回（不放 URL，避免 feedId 含 %2F 等编码字符导致签名失败）
        finderUserName: '',
        feedId: '',
        outTradeNo: '',
        ready: false,
        errMsg: '',
        weappAppid: WEAPP_APPID,
        weappEnvVersion: WEAPP_ENV_VERSION
      }
    },
    computed: {
      weappPath() {
        return WEAPP_PATH +
          '?finderUserName=' + encodeURIComponent(this.finderUserName) +
          '&feedId=' + encodeURIComponent(this.feedId)
      },
      tagTemplate() {
        // 开放标签内部模板，必须放在 <script type="text/wxtag-template"> 中。
        // 渲染为一个绿色按钮，用户点击后拉起小程序。
        return '<script type="text/wxtag-template">' +
          '<style>.launch-btn{display:block;width:560px;height:88px;line-height:88px;text-align:center;background:#07c160;color:#fff;font-size:32px;border:none;border-radius:44px;}</style>' +
          '<div class="launch-btn">进入视频号</div>' +
          '</scr' + 'ipt>'
      }
    },
    async mounted() {
      this.outTradeNo = this.getQueryString(location.href, 'out_trade_no')
      if (!this.outTradeNo) {
        this.errMsg = '缺少订单号'
        return
      }
      // 按订单号获取该商户配置的视频号参数
      try {
        const data = await getChannelsConfig(this.outTradeNo)
        if (!data || !data.finderUserName) {
          this.errMsg = '该商户未配置视频号'
          return
        }
        this.finderUserName = data.finderUserName
        this.feedId = data.feedId || ''
      } catch (e) {
        console.error('[channels] get config fail', e)
        this.errMsg = '获取视频号配置失败：' + this.stringifyErr(e)
        return
      }
      this.initWxConfig()
    },
    methods: {
      getQueryString(path, name) {
        const reg = new RegExp('(^|\\?|&)' + name + '=([^&]*)(\\s|&|$)', 'i')
        if (reg.test(path)) {
          return decodeURIComponent(RegExp.$2.replace(/\+/g, ' '))
        }
        return ''
      },
      loadJssdk() {
        return new Promise((resolve, reject) => {
          if (window.wx && window.wx.config) {
            resolve(window.wx)
            return
          }
          const script = document.createElement('script')
          script.src = JSSDK_SRC
          script.onload = () => resolve(window.wx)
          script.onerror = () => reject(new Error('JSSDK 加载失败'))
          document.head.appendChild(script)
        })
      },
      async initWxConfig() {
        let wx
        // 1) 加载 JSSDK
        try {
          wx = await this.loadJssdk()
        } catch (e) {
          console.error('[channels] load jssdk fail', e)
          this.errMsg = 'JSSDK 加载失败，请检查网络'
          return
        }
        // 2) 获取签名
        let cfg
        const signUrl = location.href.split('#')[0]
        try {
          cfg = await getJsapiSignature(signUrl)
        } catch (e) {
          console.error('[channels] get signature fail', e)
          this.errMsg = '获取签名失败：' + this.stringifyErr(e) + '（接口 /api/cashier/jssdk-signature 是否已就绪？）'
          return
        }
        if (!cfg || !cfg.appId || !cfg.signature) {
          this.errMsg = '签名返回数据不完整：' + this.stringifyErr(cfg)
          return
        }
        // 3) wx.config
        try {
          wx.config({
            debug: false,
            appId: cfg.appId,
            timestamp: cfg.timestamp,
            nonceStr: cfg.nonceStr,
            signature: cfg.signature,
            jsApiList: [],
            openTagList: ['wx-open-launch-weapp']
          })
          wx.ready(() => { this.ready = true })
          wx.error((res) => {
            console.error('[channels] wx.config error', res)
            this.errMsg = '微信配置失败：' + (res && res.errMsg ? res.errMsg : '请检查 JS 安全域名与签名')
          })
        } catch (e) {
          console.error('[channels] wx.config throw', e)
          this.errMsg = 'wx.config 异常：' + this.stringifyErr(e)
        }
      },
      stringifyErr(e) {
        if (!e) return '空'
        if (typeof e === 'string') return e
        if (e.message) return e.message
        if (e.msg) return e.msg
        try {
          return JSON.stringify(e)
        } catch (err) {
          return String(e)
        }
      },
      onLaunch() {
        console.log('[channels] launch weapp success')
      },
      onError(e) {
        console.error('[channels] launch weapp error', e && e.detail)
        this.errMsg = '打开小程序失败，请确认小程序版本与跳转配置'
      }
    }
  }
</script>

<style scoped>
  .page-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 120px 48px 80px;
    background: #f5f5f5;
    min-height: 100vh;
  }

  .logo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 40px;
  }

  .tip {
    font-size: 34px;
    color: #333;
    font-weight: 600;
    margin-bottom: 20px;
  }

  .sub {
    font-size: 26px;
    color: #999;
  }

  .hint {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* 开放标签按钮容器 */
  .launch-wrap {
    display: block;
    margin-top: 80px;
  }

  .err {
    margin-top: 40px;
    font-size: 26px;
    color: #e54d42;
    text-align: center;
    word-break: break-all;
  }
</style>
